import {
  Form,
  FormField,
  FormLabel,
  FormDescription,
  FormControl,
  FormMessage,
  FormItem,
} from "@/components/ui/form";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";

import { UserData } from "modules/authentication/types/user-data";
import {
  updateAccountEndPoint,
  settingsLocationEndPoint,
  endPointUserData,
} from "modules/main/services/endpoints";
import type { UserDataResponse } from "modules/settings/types/user-data-types";
import { toast } from "sonner";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { SectionHeader, SectionMiniHeader } from "./section-header";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetch } from "modules/main/hooks/useFetch";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { LocationResponse } from "../types/location-types";

const profileFormShema = z
  .object({
    firstName: z
      .string()
      .min(2, { message: "El nombre debe tener más de 2 carácteres" }),
    lastName: z
      .string()
      .min(2, { message: "El apellido debe tener más de 2 carácteres" }),
    city_id: z.string().min(1, { message: "Debes seleccionar una comuna" }),
    location: z.string().min(1, { message: "Debes seleccionar un sector" }),
    username: z.string().email({ message: "El email no es válido" }),
    password: z
      .string()
      .min(8, { message: "La contraseña debe tener más de 8 carácteres" })
      .regex(/[a-zA-Z]/, { message: "Debe contener al menos una letra" })
      .regex(/[0-9]/, { message: "Debe contener al menos un número" }),
    passwordConfirmation: z
      .string()
      .min(8, { message: "La contraseña debe tener más de 8 carácteres" }),
  })
  .refine(
    (data) => {
      return data.password === data.passwordConfirmation;
    },
    { message: "Las contraseñas no coinciden", path: ["passwordConfirmation"] },
  );

type profileFormvalues = z.infer<typeof profileFormShema>;

const defaultValues: Partial<profileFormvalues> = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  city_id: "",
  location: "",
  passwordConfirmation: "",
};

export const ProfileForm = () => {
  const isDesktop = useMediaQuery(isDesktopSize);
  const [selectedCityId, setSelectedCityId] = useState<string>(
    defaultValues.city_id || "",
  );
  const [filteredLocations, setFilteredLocations] = useState<
    Array<{ id: number; name: string; city_id: number }>
  >([]);
  // Toggled when server data is populated into the form; used as Select key
  // to force a remount so defaultValue takes effect (Radix reads it on mount only).
  const [dataPopulated, setDataPopulated] = useState(false);

  // Usando useFetch para obtener datos de ubicación
  const requestOptions: FetchDataOptions = {
    method: "GET",
    headers: {},
    credentials: "include",
  };

  const {
    data: locationData,
    loading: locationsLoading,
    error: locationsError,
  } = useFetch<LocationResponse>(settingsLocationEndPoint, requestOptions);

  // Fetch user profile data from the server on mount
  const {
    data: userData,
    loading: userDataLoading,
    error: userDataError,
  } = useFetch<UserDataResponse>(endPointUserData);

  const form = useForm<profileFormvalues>({
    resolver: zodResolver(profileFormShema),
    defaultValues,
    mode: "onChange",
  });

  // Guards against the watch effect resetting location during initial form
  // population: the watch effect runs after the population effect in the same
  // batch, and its stale-selectedCityId ("") would otherwise trigger a
  // location.clear because "" !== derivedCityId.
  const initialPopulatedRef = useRef(false);

  // Populate form fields when both server data and location data arrive.
  // Derive city_id from location_id so the city dropdown is selected and
  // the location dropdown has filtered options to match against.
  useEffect(() => {
    if (userData && locationData && !initialPopulatedRef.current) {
      initialPopulatedRef.current = true;

      const matchedLocation = locationData.locations.find(
        (loc) => loc.id === userData.location_id,
      );
      const cityId = matchedLocation?.city_id?.toString() || "";

      form.reset({
        firstName: userData.first_name,
        lastName: userData.last_name,
        username: userData.email,
        city_id: cityId,
        location: userData.location_id?.toString() || "",
        password: "",
        passwordConfirmation: "",
      });

      setDataPopulated(true);
    }
  }, [userData, locationData, form]);

  // Determine if the form should be disabled while user data is loading
  const isLoadingUserData = userDataLoading && !userData;

  // Procesar datos cuando se cargan desde el endpoint
  useEffect(() => {
    if (locationData) {
      // Si hay un cityId seleccionado, filtrar las locations correspondientes
      if (selectedCityId) {
        const cityLocations = locationData.locations.filter(
          (loc) => loc.city_id === parseInt(selectedCityId),
        );
        setFilteredLocations(cityLocations);
      }
    }
  }, [locationData, selectedCityId]);

  // Watch for city changes to filter locations.
  // During initial population the ref guards against resetting the already-set
  // location value — the stale selectedCityId (initial "") would otherwise
  // trigger a location.clear because "" !== derivedCityId.
  useEffect(() => {
    const cityId = form.watch("city_id");

    if (cityId && locationData?.locations) {
      setSelectedCityId(cityId);
      const cityLocations = locationData.locations.filter(
        (loc) => loc.city_id === parseInt(cityId),
      );
      setFilteredLocations(cityLocations);

      // Reset location selection if the city changes via user interaction
      if (cityId !== selectedCityId) {
        if (initialPopulatedRef.current) {
          initialPopulatedRef.current = false;
        } else {
          form.setValue("location", "");
        }
      }
    }
  }, [form, selectedCityId, locationData, form.watch("city_id")]);

  // Log any errors from fetches (both user data and locations)
  useEffect(() => {
    if (userDataError) {
      // User data fetch failed — form will use empty defaultValues as fallback
    }
    if (locationsError) {
      // Locations fetch failed — city/location selects will show loading state
    }
  }, [userDataError, locationsError]);

  const updateLocalStorage = (data: profileFormvalues) => {
    const existing = JSON.parse(
      localStorage.getItem("userData") ?? "{}",
    ) as UserData;

    existing.first_name = data.firstName;
    existing.last_name = data.lastName;
    existing.username = data.username;
    existing.city_id = parseInt(data.city_id);
    existing.location_id = parseInt(data.location);

    // Find the selected city and location to store their names
    const selectedCity = locationData?.cities.find(
      (city) => city.id === parseInt(data.city_id),
    );
    const selectedLocation = locationData?.locations.find(
      (loc) => loc.id === parseInt(data.location),
    );

    if (selectedCity) {
      existing.city_name = selectedCity.name;
    }

    if (selectedLocation) {
      existing.location_name = selectedLocation.name;
    }

    localStorage.setItem("userData", JSON.stringify(existing));
  };

  const onSubmit = async (data: profileFormvalues) => {
    try {
      const formData = {
        ...data,
        // city_id: parseInt(data.city_id),
        location: parseInt(data.location),
      };

      const response = await fetch(updateAccountEndPoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Error al actualizar los datos");
      }

      const responseData = (await response.json()) as {
        status: boolean;
        message?: string;
      };
      if (responseData.status) {
        toast("Tus datos han sido actualizados correctamente");
        updateLocalStorage(data);
      } else {
        toast(
          responseData.message ||
            "Ha ocurrido un error al actualizar los datos",
        );
      }
    } catch (e) {
      toast("Ha ocurrido un error al actualizar los datos");
    }
  };

  return (
    <>
      {!isDesktop && (
        <SectionHeader
          title="Ajustes de cuenta"
          description=" Manten actualizado los datos de tú cuenta"
          className="mb-4"
        />
      )}
      <Form {...form}>
        {isDesktop && (
          <SectionMiniHeader
            className="mb-4"
            title="Ajustes de cuenta"
            description="Manten actualizado los datos de tú cuenta"
          />
        )}

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col space-y-4 mt-4"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre</FormLabel>
                <FormControl>
                  <Input placeholder="Nombre" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apellidos</FormLabel>
                <FormControl>
                  <Input placeholder="Apellidos" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email" type="email" {...field} />
                </FormControl>
                <FormDescription>
                  Esto se usará como tu nombre de usuario.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* City (Comuna) field */}
          <FormField
            control={form.control}
            name="city_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comuna</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    key={`city-${dataPopulated}`}
                    disabled={locationsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tu comuna" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationsLoading ? (
                        <div className="flex justify-center p-2">
                          <div className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"></div>
                        </div>
                      ) : (
                        <SelectGroup>
                          {locationData?.cities?.map((city) => (
                            <SelectItem
                              key={city.id}
                              value={city.id.toString()}
                            >
                              {city.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Location (sector) field */}
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sector</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    key={`loc-${dataPopulated}`}
                    disabled={
                      locationsLoading ||
                      !selectedCityId ||
                      filteredLocations.length === 0
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tu sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationsLoading ? (
                        <div className="flex justify-center p-2">
                          <div className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"></div>
                        </div>
                      ) : (
                        <SelectGroup>
                          {filteredLocations.map((location) => (
                            <SelectItem
                              key={location.id}
                              value={location.id.toString()}
                            >
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Selecciona primero una comuna para ver los sectores
                  disponibles
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contraseña</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirmar Contraseña</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isLoadingUserData}>
            Actualizar
          </Button>
        </form>
      </Form>
    </>
  );
};
